import { createContext, PropsWithChildren, useContext, useMemo, useRef } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { useUserInfo } from "./user-info";
import { isHttpError, http } from "@mb-pro-ui/utils";

function httpWithAuth<T = unknown>(token: string, path: string, options?: RequestInit) {
  const headers = new Headers(options?.headers);
  headers.set("content-type", "application/json");
  headers.set("token", token);
  if (!options) {
    options = { headers };
  } else {
    options.headers = headers;
  }
  return http<T>(path, options);
}

type BaseUrl = string;

const BaseUrlContext = createContext<BaseUrl | undefined>(undefined);

export const useBaseUrl = () => {
  const baseUrl = useContext(BaseUrlContext);
  if (baseUrl === undefined) {
    throw new Error("useBaseUrl must be used within an BaseUrlProvider");
  }
  return baseUrl;
};

type Api = <T>(path: string, options?: RequestInit) => Promise<T>;

const ApiContext = createContext<Api | undefined>(undefined);

export type ApiProviderProps = PropsWithChildren<{ baseUrl: string }>;

export const ApiProvider = ({ baseUrl, children }: ApiProviderProps) => {
  const [userInfo, setUserInfo] = useUserInfo();
  const client = useRef(new QueryClient());

  const api = useMemo(() => {
    if (userInfo) {
      return async <T,>(path: string, options?: RequestInit) => {
        try {
          return await httpWithAuth<T>(userInfo.token, `${baseUrl}${path}`, options);
        } catch (error) {
          if (isHttpError(error) && error.status === 401 && (error.data?.type === undefined || error.data.type === "token")) {
            setUserInfo(null);
          }
          throw error;
        }
      };
    } else {
      return <T,>(path: string, options?: RequestInit) => http<T>(`${baseUrl}${path}`, options);
    }
  }, [userInfo, baseUrl, setUserInfo]);

  return (
    <QueryClientProvider client={client.current}>
      <BaseUrlContext.Provider value={baseUrl}>
        <ApiContext.Provider value={api}>{children}</ApiContext.Provider>
      </BaseUrlContext.Provider>
    </QueryClientProvider>
  );
};

export const useApi = () => {
  const api = useContext(ApiContext);
  if (api === undefined) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return api;
};
